import { describeQueryShape } from '@aibi/analysis-engine';
import type {
  AnalysisState,
  AxisSpec,
  ConditionalFormatRule,
  FormatSpec,
  VisualizationSpec,
  VisualizationStyle,
  VisualizationType,
} from '@aibi/contracts';
import type { SemanticModelIndex } from '@aibi/semantic-model';
import type { Registry } from '@aibi/shared';
import {
  checkCompatibility,
  suggestEncodings,
  type VisualizationDescriptor,
} from '@aibi/visualization-engine';
import { accept, reject, type StepResult } from './rejection';

/**
 * Cambia el tipo de visualizacion. Solo cambian `visualization.type` y sus encodings:
 * dimensiones, medidas, filtros, orden y selecciones (la intencion analitica) se conservan.
 */
export function setVisualizationType(state: AnalysisState, type: VisualizationType): StepResult {
  if (state.visualization.type === type) return accept(state);
  return accept({
    ...state,
    visualization: { ...state.visualization, type, encodings: suggestEncodings(type, state) },
  });
}

/** Cambios de presentacion admitidos desde el panel de propiedades. */
export interface VisualizationPatch {
  readonly legend?: Partial<VisualizationSpec['legend']>;
  readonly labels?: Partial<VisualizationSpec['labels']>;
  readonly tooltip?: { readonly show?: boolean };
  readonly stacking?: VisualizationSpec['grouping']['stacking'];
  readonly axes?: {
    readonly x?: { readonly show?: boolean };
    readonly y?: { readonly show?: boolean };
  };
  /** Formato de presentacion de un campo; `null` restablece el formato del modelo. */
  readonly fieldFormat?: { readonly key: string; readonly format: FormatSpec | null };
  readonly interaction?: Partial<VisualizationSpec['interaction']>;
  /** Tipografia y colores; `null` en un color lo restaura al tema. */
  readonly style?: {
    readonly fontSize?: VisualizationStyle['fontSize'];
    readonly foreground?: string | null;
    readonly background?: string | null;
    readonly palette?: readonly string[];
  };
  /** Sustituye por completo las reglas de formato condicional. */
  readonly conditional?: readonly ConditionalFormatRule[];
}

const axisWith = (
  current: AxisSpec | undefined,
  show: boolean | undefined,
): AxisSpec | undefined =>
  show === undefined ? current : { ...(current ?? { show: true, scale: 'auto' }), show };

const applyStylePatch = (
  current: VisualizationStyle,
  patch: NonNullable<VisualizationPatch['style']>,
): VisualizationStyle => {
  const next: VisualizationStyle = {
    fontSize: patch.fontSize ?? current.fontSize,
    palette: patch.palette === undefined ? [...current.palette] : [...patch.palette],
  };
  const foreground =
    patch.foreground === undefined ? current.foreground : (patch.foreground ?? undefined);
  const background =
    patch.background === undefined ? current.background : (patch.background ?? undefined);
  if (foreground !== undefined) Reflect.set(next, 'foreground', foreground);
  if (background !== undefined) Reflect.set(next, 'background', background);
  return next;
};

export function updateVisualization(state: AnalysisState, patch: VisualizationPatch): StepResult {
  const viz = state.visualization;
  let formatting = viz.formatting;
  if (patch.fieldFormat) {
    const { key, format } = patch.fieldFormat;
    if (
      !state.measures.some((m) => m.key === key) &&
      !state.dimensions.some((d) => d.key === key)
    ) {
      return reject('unknown-field', 'El campo no forma parte del analisis.');
    }
    const fields = { ...formatting.fields };
    if (format === null) Reflect.deleteProperty(fields, key);
    else fields[key] = format;
    formatting = { ...formatting, fields };
  }
  if (patch.conditional !== undefined) {
    const known = new Set([
      ...state.measures.map((measure) => measure.key),
      ...state.dimensions.map((dimension) => dimension.key),
    ]);
    for (const rule of patch.conditional) {
      if (!known.has(rule.key)) {
        return reject('unknown-field', 'La regla condicional apunta a un campo inexistente.');
      }
    }
    formatting = { ...formatting, conditional: [...patch.conditional] };
  }
  const x = axisWith(viz.axes.x, patch.axes?.x?.show);
  const y = axisWith(viz.axes.y, patch.axes?.y?.show);
  const next: VisualizationSpec = {
    ...viz,
    legend: { ...viz.legend, ...patch.legend },
    labels: { ...viz.labels, ...patch.labels },
    tooltip: { ...viz.tooltip, ...patch.tooltip },
    grouping: { ...viz.grouping, stacking: patch.stacking ?? viz.grouping.stacking },
    axes: { ...viz.axes, ...(x ? { x } : {}), ...(y ? { y } : {}) },
    formatting,
    interaction: { ...viz.interaction, ...patch.interaction },
    style: patch.style === undefined ? viz.style : applyStylePatch(viz.style, patch.style),
  };
  return accept({ ...state, visualization: next });
}

export interface VisualizationOption {
  readonly descriptor: VisualizationDescriptor;
  readonly compatible: boolean;
  readonly reasons: readonly string[];
}

/** Tipos registrados con su compatibilidad frente a la forma actual de la consulta. */
export function visualizationOptions(
  state: AnalysisState,
  index: SemanticModelIndex,
  registry: Registry<VisualizationDescriptor>,
): VisualizationOption[] {
  const shape = describeQueryShape(state, index);
  return registry.list().map((descriptor) => {
    const { compatible, reasons } = checkCompatibility(descriptor, shape);
    return { descriptor, compatible, reasons };
  });
}

/**
 * Reasigna los encodings segun los campos actuales y elimina de la visualizacion y del
 * analisis cualquier referencia a claves que ya no existen. Garantiza que el estado
 * resultante siga siendo coherente tras quitar o reordenar campos.
 */
export function normalizeCard(state: AnalysisState): AnalysisState {
  const dimensionKeys = new Set(state.dimensions.map((d) => d.key));
  const measureKeys = new Set(state.measures.map((m) => m.key));
  const has = (key: string): boolean => dimensionKeys.has(key) || measureKeys.has(key);
  const viz = state.visualization;

  const fields = Object.fromEntries(
    Object.entries(viz.formatting.fields).filter(([key]) => has(key)),
  );
  const { seriesKey } = viz.grouping;
  const visualization: VisualizationSpec = {
    ...viz,
    encodings: suggestEncodings(viz.type, state),
    grouping: {
      stacking: viz.grouping.stacking,
      ...(seriesKey !== undefined && has(seriesKey) ? { seriesKey } : {}),
    },
    sorting: viz.sorting.filter((sort) => has(sort.key)),
    formatting: {
      ...viz.formatting,
      fields,
      conditional: viz.formatting.conditional.filter((rule) => has(rule.key)),
    },
    tooltip: { ...viz.tooltip, fields: viz.tooltip.fields.filter(has) },
  };

  return {
    ...state,
    sorting: state.sorting.filter((sort) => has(sort.key)),
    grouping: {
      ...state.grouping,
      rollupKeys: state.grouping.rollupKeys.filter((key) => dimensionKeys.has(key)),
    },
    comparison: state.comparison
      ? {
          ...state.comparison,
          measureKeys: state.comparison.measureKeys.filter((key) => measureKeys.has(key)),
        }
      : null,
    visualization,
  };
}
